/**
 * API client for underwriter operations
 * Calls API Gateway with Cognito JWT auth
 */

import type { Deal, DealInput, UserCriteria } from './schema'

const API_BASE = import.meta.env.VITE_API_URL

if (!API_BASE) {
  console.warn('VITE_API_URL is not set - API calls will fail')
}

interface ApiError {
  error: string
}

type GetAccessTokenFn = () => Promise<string | null>

let getAccessTokenFn: GetAccessTokenFn | null = null

/**
 * Set the function to get the access token (called from AuthProvider)
 */
export function setAccessTokenGetter(fn: GetAccessTokenFn): void {
  getAccessTokenFn = fn
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  // Add Authorization header
  if (getAccessTokenFn) {
    const token = await getAccessTokenFn()
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
    }
  }

  const res = await fetch(url, {
    ...options,
    headers,
  })

  if (!res.ok) {
    const error: ApiError = await res.json().catch(() => ({ error: res.statusText }))

    // Handle 401 by redirecting to login
    if (res.status === 401 && typeof window !== 'undefined') {
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`
      throw new Error('Unauthorized')
    }

    throw new Error(error.error || `API error: ${res.status}`)
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return {} as T
  }

  return res.json()
}

// Property lookup response from RapidAPI
export interface PropertyLookupResult {
  price?: number
  bedrooms?: number
  bathrooms?: number
  sqft?: number
  yearBuilt?: number
  propertyType?: string
  propertyTax?: number
  rentEstimate?: number
}

export const api = {
  deals: {
    /**
     * List all deals for current user
     */
    list: async (): Promise<{ deals: Deal[] }> => {
      return fetchApi<{ deals: Deal[] }>('/deals')
    },

    /**
     * Get a single deal with calculated metrics
     */
    get: async (id: string): Promise<{ deal: Deal }> => {
      return fetchApi<{ deal: Deal }>(`/deals/${id}`)
    },

    /**
     * Create a new deal
     */
    create: async (deal: DealInput): Promise<{ deal: Deal }> => {
      return fetchApi<{ deal: Deal }>('/deals', {
        method: 'POST',
        body: JSON.stringify(deal),
      })
    },

    /**
     * Update a deal
     */
    update: async (id: string, deal: Partial<DealInput>): Promise<{ deal: Deal }> => {
      return fetchApi<{ deal: Deal }>(`/deals/${id}`, {
        method: 'PUT',
        body: JSON.stringify(deal),
      })
    },

    /**
     * Delete a deal
     */
    delete: async (id: string): Promise<void> => {
      await fetchApi<void>(`/deals/${id}`, {
        method: 'DELETE',
      })
    },
  },

  criteria: {
    /**
     * Get user's go/no-go criteria
     */
    get: async (): Promise<{ criteria: UserCriteria | null }> => {
      return fetchApi<{ criteria: UserCriteria | null }>('/criteria')
    },

    /**
     * Update user's criteria
     */
    update: async (criteria: UserCriteria): Promise<{ criteria: UserCriteria }> => {
      return fetchApi<{ criteria: UserCriteria }>('/criteria', {
        method: 'PUT',
        body: JSON.stringify(criteria),
      })
    },
  },

  property: {
    /**
     * Lookup property data from RapidAPI
     */
    lookup: async (address: string, city: string, state: string): Promise<PropertyLookupResult> => {
      return fetchApi<PropertyLookupResult>('/property/lookup', {
        method: 'POST',
        body: JSON.stringify({ address, city, state }),
      })
    },
  },
}

// Convenience wrapper for common operations
export const apiClient = {
  ...api,
  lookupProperty: api.property.lookup,
}
